// material-ui
import { useTheme } from '@mui/material/styles';
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { DefaultAgGridTooltip } from '../../components/ag-grid/DefaultAgGridTooltip';
import MainCard from '../../components/MainCard';
import ScrollX from '../../components/ScrollX';
import { Backdrop, Box, Chip, CircularProgress, IconButton, OutlinedInput, Stack, Tooltip, Typography, useMediaQuery } from '@mui/material';
import useConfig from '../../hooks/useConfig';

// AgGrid
import { AgGridReact } from 'ag-grid-react';
import { ColumnMovedEvent, ColumnResizedEvent, ColumnVisibleEvent, SortChangedEvent, FilterModel, GridReadyEvent, PaginationChangedEvent, RowClickedEvent, ColDef } from 'ag-grid-community';
import { GridApi, ICellRendererParams, IServerSideDatasource, IServerSideGetRowsParams, RowNode, SideBarDef } from 'ag-grid-enterprise';
import 'ag-grid-enterprise/styles/ag-grid.css';
import 'ag-grid-enterprise/styles/ag-theme-material.css';
import 'assets/third-party/ag-theme-material-dark.scss';
import { FilterOutlined, SearchOutlined } from '@ant-design/icons';
import useWindowDimensions from '../../hooks/useWindowDimensions';
import { ApplicationStatusChip } from 'sections/offers/ApplicationStatusChip';

import { DesktopDatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import dayjs from 'utils/dayjsSetup';
import { getFormattedDateTimeUTC } from 'utils/getFormattedDate';
import { AuditEvent, GridType, Rights, SortArgs, useAuditLogsLazyQuery, User } from '../../graphql/types';
import UserCellRenderer from '../../components/ag-grid/UserCellRenderer';
import { McaStatusChip } from '../../sections/merchants/McaStatusChip';
import InvestorDropDownAutoComplete from '../../components/InvestorDropDownAutoComplete';
import McaListTools from '../../sections/merchants/McaListTools';
import useAuth from '../../hooks/useAuth';
import useAgGridOutsideClick from '../../components/ag-grid/useAgGridOutsideClick';
import { applyGridColumnState } from 'utils/agGridUtils';
interface AuditFilterModel {
  type?: { filterType: string; values: string[] };
  fromDate?: { filterType: string; type: string; filter: string };
  toDate?: { filterType: string; type: string; filter: string };
  userFilter?: { filterType: string; type: string; filter: string };
  search?: { filterType: string; type: string; filter: string };
}

const AuditListFilterContext = createContext<{
  filterState: FilterModel | null;
  setFilterState: (filters: FilterModel | null) => void;
}>({
  filterState: null,
  setFilterState: () => { }
});

export const AuditListFilterProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [filterState, setFilterState] = useState<FilterModel | null>(null);
  return <AuditListFilterContext.Provider value={{ filterState, setFilterState }}>{children}</AuditListFilterContext.Provider>;
};
const McaNoStatusChipCellRenderer = ({ node, data }: ICellRendererParams) => {
  if (!data.mca) {
    return null;
  }
  return <McaStatusChip id={data.mca.id} status={data.mca.status} label={data.mca.number} navigateToDetails={true} />;
};

const DetailCellRenderer = (params: ICellRendererParams) => {
  const { data } = params;
  return (
    <Box sx={{ padding: 2 }}>
      <Typography variant="subtitle1">Payload</Typography>
      <pre style={{ whiteSpace: 'pre-wrap', wordWrap: 'break-word' }}>{JSON.stringify(data.payload, null, 2)}</pre>
    </Box>
  );
};

const AuditList = () => {
  const theme = useTheme();
  const { mode } = useConfig();
  const matchDownSM = useMediaQuery(theme.breakpoints.down('sm'));
  const { user: currentUser } = useAuth();
  const userId = currentUser?.id;
  const keyName = `${userId}_AUDIT_LIST_GRID`;
  const perPage = 50;
  const useScroll = localStorage.getItem(keyName + '_USE_SCROLL') === 'false' ? null : true;
  const [refreshKey, setRefreshKey] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(parseInt(localStorage.getItem(keyName + '_ROWS_PER_PAGE_STATE') || '') || perPage);
  const gridStyle = useMemo(() => ({ height: '100%', width: '100%' }), []);
  const [currentTotalRows, setCurrentTotalRows] = useState<number>(0);
  const [currentPageRows, setCurrentPageRows] = useState<number>(0);
  const [searchValue, setSearchValue] = useState('');
  const [gridApi, setGridApi] = useState<GridApi | null>(null);
  const [backdropOpen, setBackdropOpen] = useState<boolean>(false);
  const [fromDateValue, setFromDateValue] = useState(dayjs().utc().subtract(3, 'month').toDate());
  const [toDateValue, setToDateValue] = useState(dayjs().utc().add(1, 'days').toDate());
  const [isFilterApplied, setIsFilterApplied] = useState<boolean>(false);
  const { filterState, setFilterState } = useContext(AuditListFilterContext);
  const typeFilter = filterState?.type || '';
  const gridApiRef = useRef<GridApi>();
  const [fetchData] = useAuditLogsLazyQuery({
    fetchPolicy: 'network-only'
  });

  const getRowClass = (params: any) => {
    if (params.node.rowPinned === 'bottom') {
      return 'ag-pin-bottom-row-' + mode;
    }
  };

  const defaultColDef = useMemo(() => {
    return {
      suppressHeaderMenuButton: true,
      editable: false,
      sortable: true,
      resizable: true,
      pivot: true,
      filter: false,
      autoHeaderHeight: true,
      columnChooserParams: {
        suppressColumnFilter: true,
        contractColumnSelection: true
      },
      tooltipComponent: DefaultAgGridTooltip
    };
  }, []);

  const refreshGrid = useCallback(() => {
    if (gridApi) {
      gridApi.refreshServerSide();
    }
    setRefreshKey((prevKey) => prevKey + 1);
  }, [gridApi]);

  const sideBar = useMemo<SideBarDef>(() => {
    return {
      position: 'right',
      toolPanels: [
        {
          id: 'columns',
          labelDefault: 'Columns',
          labelKey: 'columns',
          iconKey: 'columns',
          toolPanel: 'agColumnsToolPanel',
          toolPanelParams: {
            suppressRowGroups: true,
            suppressValues: true,
            suppressPivots: true,
            suppressPivotMode: true,
            suppressColumnFilter: true,
            suppressColumnSelectAll: true,
            suppressColumnExpandAll: true
          }
        },
        {
          id: 'filters',
          labelDefault: 'Filters',
          labelKey: 'filters',
          iconKey: 'filter',
          toolPanel: 'agFiltersToolPanel',
          toolPanelParams: {
            suppressFilterSearch: true
          }
        },
        {
          id: 'settings',
          labelDefault: 'Settings',
          labelKey: 'settings',
          iconKey: 'menu',
          toolPanel: McaListTools,
          toolPanelParams: {
            preferencesKey: GridType.AuditListGrid,
            refreshGrid,
            keyName
          }
        }
      ]
    };
  }, []);

  const onFromDateChange = (value: string) => {
    if (!gridApi) {
      return;
    }
    setFromDateValue(dayjs(value).utc().toDate());
    const fm = gridApi.getFilterModel();
    fm['fromDate'] = {
      filterType: 'date',
      type: 'contains',
      filter: value
    };
    gridApi.setFilterModel(fm);
    gridApi.onFilterChanged();
    setFilterState({ ...filterState, fromDate: { filterType: 'date', type: 'contains', filter: value } } as AuditFilterModel);
  };

  const onToDateChange = (value: string) => {
    if (!gridApi) {
      return;
    }
    setToDateValue(dayjs(value).utc().toDate());
    const fm = gridApi.getFilterModel();
    fm['toDate'] = {
      filterType: 'date',
      type: 'contains',
      filter: value
    };
    gridApi.setFilterModel(fm);
    gridApi.onFilterChanged();
    setFilterState({ ...filterState, toDate: { filterType: 'date', type: 'contains', filter: value } } as AuditFilterModel);

  };
  const [columnDefs] = useState<ColDef[]>([
    {
      headerName: 'Type',
      field: 'type',
      filter: true,
      filterParams: {
        values: [
          'MCA_STATUS_CHANGE',
          'MCA_EDIT',
          'MCA_START',
          'MCA_STATUS_MANAGEMENT_CHANGE',
          'INTEGRATION_RELOAD_TRANSACTIONS',
          'MCA_RELOAD_TRANSACTIONS',
          'RECURRENCE_START',
          'RECURRENCE_UPDATE',
          'RECURRENCE_PAUSE',
          'RECURRENCE_RESUME',
          'RECURRENCE_STOP',
          'RECURRENCE_ADD_ATTEMPT',
          'RECURRENCE_ADD',
          'RECURRENCE_RELOAD_TRANSACTIONS',
          'MANUAL_TRANSACTION_ADD',
          'MANUAL_TRANSACTION_EDIT',
          'MANUAL_TRANSACTION_DELETE',
          'CASH_TRANSACTION_EDIT',
          'CASH_TRANSACTION_ADD',
          'CASH_TRANSACTION_DELETE',
          'TRANSACTION_CANCEL_ATTEMPT',
          'LOGIN',
          'LOGIN_AS',
          'LOGIN_ATTEMPT',
          'FORGOT_PASSWORD',
          'PASSWORD_RESET',
          'SYNDICATION_DELETE',
          'SYNDICATION_EDIT',
          'SYNDICATION_ADD',
          'APPLICATION_ADD',
          'APPLICATION_EDIT',
          'APPLICATION_DELETE',
          'OFFER_SEND',
          'RECONCILIATION_CREATED',
          'RECONCILIATION_DELETED',
          'EMAIL_SENT',
          'EXPERIAN_CREDIT_REPORT_REQUESTED',
          'EXPERIAN_CREDIT_REPORT_DOWNLOADED'
        ].sort()
      }
    },
    {
      headerName: 'Description',
      field: 'description',
      width: 400,
      tooltipField: 'description',
    },
    {
      headerName: 'App#',
      field: 'application.number',
      sortable: false,
      headerClass: 'ag-header-cell-limegreen-center',
      toolPanelClass: ['ag-header-cell-limegreen-center'],
      cellClass: 'ag-centered-cell',
      cellRenderer: ({ data }: ICellRendererParams) => {
        if (!data || !data.application) return null;

        return (
          <ApplicationStatusChip
            id={data.application?.id || ''}
            label={data.application.number || ''}
            color={data.application?.status?.color || ''}
            navigateToDetails={true}
            detailsPath="/applications"
            description={data.application?.status?.description || ''}
          />
        );
      }
    },
    {
      type: 'rightAligned',
      resizable: false,
      headerName: 'MCA#',
      field: 'mca.number',
      sortable: false,
      cellRenderer: McaNoStatusChipCellRenderer
    },
    {
      headerName: 'Actioned By',
      field: 'user.email',
      sortable: true,
      cellRenderer: ({ data }: ICellRendererParams) => {
        if (!data.user) {
          return null;
        }
        return (
          <Stack direction="row" alignItems="center" height="100%">
            <UserCellRenderer data={data.user} spacing={1.5} />{' '}
          </Stack>
        );
      }
    },
    { headerName: 'IP Address', field: 'ipAddress' },
    {
      headerName: 'Created At',
      sort: 'desc',
      field: 'createdAt',
      valueFormatter: (params) => getFormattedDateTimeUTC(params.value)
    },
    {
      field: 'fromDate',
      hide: true,
      filter: 'agTextColumnFilter',
      lockVisible: true,
      initialHide: true,
      suppressNavigable: true,
      suppressHeaderMenuButton: true,
      suppressColumnsToolPanel: true,
      suppressFiltersToolPanel: true
    },
    {
      field: 'toDate',
      hide: true,
      filter: 'agTextColumnFilter',
      lockVisible: true,
      initialHide: true,
      suppressNavigable: true,
      suppressHeaderMenuButton: true,
      suppressColumnsToolPanel: true,
      suppressFiltersToolPanel: true
    },
    {
      field: 'userFilter',
      hide: true,
      filter: 'agTextColumnFilter',
      lockVisible: true,
      initialHide: true,
      suppressNavigable: true,
      suppressHeaderMenuButton: true,
      suppressColumnsToolPanel: true,
      suppressFiltersToolPanel: true
    },
    {
      field: 'search',
      hide: true,
      aggFunc: null,
      enablePivot: false,
      pivot: false,
      lockVisible: true,
      initialHide: true,
      suppressNavigable: true,
      suppressHeaderMenuButton: true,
      suppressColumnsToolPanel: true,
      suppressFiltersToolPanel: true,
      filter: 'agTextColumnFilter',
      filterParams: {
        newRowsAction: 'keep'
      }
    }
  ]);

  const { height, width } = useWindowDimensions();
  const onFilterChanged = useCallback(() => {
    if (!gridApiRef.current) return;
    const filterModel = gridApiRef.current.getFilterModel();
    setFilterState(filterModel);
  }, []);
  const onGridReady = useCallback(
    ({ api }: GridReadyEvent) => {
      setGridApi(api);
      applyGridColumnState(api, keyName);
      let filterModel: AuditFilterModel | null = null;
      if (filterState) {
        filterModel = filterState as AuditFilterModel;
      } else if ((filterState as AuditFilterModel || null)?.type || (filterState as AuditFilterModel || null)?.fromDate || (filterState as AuditFilterModel || null)?.toDate) {
        filterModel = {};
        if (typeFilter) {
          filterModel.type = {
            values: [typeFilter],
            filterType: 'set'
          };
        }
        if (filterModel.fromDate) {
          setFromDateValue(dayjs(filterModel.fromDate.filter).utc().toDate());
        }
        if (filterModel.toDate) {
          setToDateValue(dayjs(filterModel.toDate.filter).utc().toDate());
        }
      } else {
        const savedFilters = localStorage.getItem(keyName + '_FILTER_STATE');
        if (savedFilters) {
          filterModel = JSON.parse(savedFilters);
        }
      }

      if (filterModel) {
        api.setFilterModel(filterModel);
        setFilterState(filterModel);

        if (filterModel.search?.filter) {
          setSearchValue(filterModel.search.filter);
        }
      }
      api.addEventListener('filterChanged', onFilterChanged);
      gridApiRef.current = api;
      api.updateGridOptions({
        serverSideDatasource
      });
    },
    [gridApi, filterState]
  );
  useAgGridOutsideClick(gridApi);
  const onGridPreDestroyed = useCallback(() => {
    gridApiRef.current = undefined;
  }, []);
  useEffect(() => {
    return () => {
      gridApi?.removeEventListener('filterChanged', onFilterChanged);
    };
  }, [onFilterChanged]);
  useEffect(() => {
    if (gridApi) {
      const fm = gridApi.getFilterModel();
      if (fm['fromDate']) {
        const fromDate = fm['fromDate'].filter;
        setFromDateValue(dayjs(fromDate).utc().toDate());
      }
      if (fm['toDate']) {
        const toDate = fm['toDate'].filter;
        setToDateValue(dayjs(toDate).utc().toDate());
      }
    }
  }, [gridApi]);

  const onPageSizeChanged = useCallback((params: PaginationChangedEvent) => {
    if (!params.api.paginationGetPageSize() || useScroll) {
      return;
    }
    localStorage.setItem(keyName + '_ROWS_PER_PAGE_STATE', params.api.paginationGetPageSize() + '');
  }, []);
  const saveColumnsStateOnChange = useCallback(
    (event: ColumnResizedEvent | SortChangedEvent | ColumnMovedEvent | ColumnVisibleEvent) => {
      if (
        gridApi && event.source?.toLowerCase().includes('ui') &&
        ((event.type === 'columnResized' && event.finished) ||
          (event.type === 'columnMoved' && event.finished) ||
          event.type === 'sortChanged' ||
          event.type === 'columnVisible')
      ) {
        if (event.type === 'columnVisible') {
          const colId = event.column?.getColId();
          if (colId && event.column?.isVisible()) {
            applyGridColumnState(gridApi, keyName, { delay: 100 });
          }
        }
        const columnsState = JSON.stringify(gridApi.getColumnState());
        localStorage.setItem(keyName + '_COLUMNS_STATE', columnsState);
      }
    },
    [gridApi]
  );

  const onSearchChange = (value: string) => {
    if (!gridApi) {
      return;
    }
    setSearchValue(value);
    const fm = gridApi.getFilterModel();
    fm['search'] = {
      filterType: 'text',
      type: 'contains',
      filter: value
    };
    gridApi.setFilterModel(fm);
    gridApi.onFilterChanged();
  };

  const gridHeight = useMemo<number>(() => {
    let h = 500;
    if (currentPageRows >= 1) {
      h = height > 800 ? height - 160 : 500;
      h = Math.min(currentPageRows * 47.77 + 210, h);
      if (h < 500) {
        h = 500;
      }
    }
    return h;
  }, [height, currentPageRows]);

  const serverSideDatasource: IServerSideDatasource = {
    getRows: async (params: IServerSideGetRowsParams) => {
      if (params.api.isDestroyed()) {
        return;
      }
      const filters = params.request.filterModel || {};
      const hasTypeFilter = 'type' in filters && filters.type.values?.length > 0;
      if (hasTypeFilter) {
        setIsFilterApplied(true);
      } else setIsFilterApplied(false);
      const fm = params.request.filterModel as AuditFilterModel;
      const res = await fetchData({
        variables: {
          limit: (params.request.endRow || 0) - (params.request.startRow || 0),
          offset: params.request.startRow,
          search: fm?.search?.filter,
          eventTypes: fm?.type?.values,
          userId: fm?.userFilter?.filter,
          fromDate: fm?.fromDate?.filter,
          toDate: fm?.toDate?.filter,
          sortArgs: params.request.sortModel?.map((x) => ({ field: x.colId, direction: x.sort }) as SortArgs)
        }
      });
      if (params.api.isDestroyed()) {
        return;
      }
      const rows = res.data?.auditLogs.data || [];
      const total = res.data?.auditLogs.total || 0;
      setCurrentTotalRows(total);
      setCurrentPageRows(rows.length);
      params.success({
        rowData: rows,
        rowCount: total
      });
      applyGridColumnState(params.api, keyName, { delay: 100 });
    }
  };

  const handleUserFilterChange = (selectedUser: Partial<User> | null) => {
    if (gridApi) {
      const currentFilterModel = gridApi.getFilterModel();
      gridApi.setFilterModel({
        ...currentFilterModel,
        userFilter: { filterType: 'text', type: 'contains', filter: selectedUser?.id || '' }
      });
      gridApi.onFilterChanged();
    }
  };

  const onRowClicked = useCallback((event: RowClickedEvent<AuditEvent>) => {
    if (event.node.master) {
      event.node.setExpanded(!event.node.expanded);
    }
  }, []);

  const detailCellRenderer = useMemo<any>(() => {
    return DetailCellRenderer;
  }, []);
  const resetFilters = () => {
    if (gridApi) {
      gridApi.setFilterModel({
        type: null
      });
      gridApi.onFilterChanged();
    }
    setIsFilterApplied(false);
  };
  return (
    <MainCard content={false}>
      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={backdropOpen}>
        <CircularProgress color="inherit" />
      </Backdrop>
      <ScrollX>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Stack
            direction={matchDownSM ? 'column' : 'row'}
            spacing={1}
            justifyContent="space-between"
            alignItems="center"
            sx={{ pl: 3, pr: 3, pt: 1.5, pb: 1.5 }}
          >
            <Stack direction={matchDownSM ? 'column' : 'row'} spacing={1} alignItems="center">
              <Typography variant="caption" color="secondary">
                Search
              </Typography>
              <OutlinedInput
                value={searchValue || ''}
                onChange={(e) => {
                  onSearchChange(e.target.value);
                }}
                placeholder={`Search ${currentTotalRows} records...`}
                startAdornment={<SearchOutlined />}
                size="small"
                sx={{ minWidth: '180px', pt: 0.125, pb: 0.125 }}
              />
              <Stack sx={{ width: 240 }}>
                <InvestorDropDownAutoComplete
                  onInvestorChange={handleUserFilterChange}
                  rights={[
                    Rights.IsInvestor,
                    Rights.IsSuperAdmin,
                    Rights.IsBroker,
                    Rights.IsIsoManager,
                    Rights.IsFunder,
                    Rights.IsLegal,
                    Rights.IsMerchant,
                    Rights.IsSubmission,
                    Rights.IsSubmissionAdmin
                  ]}
                />
              </Stack>
              <Typography variant="caption" color="secondary" sx={{ pl: 6 }}>
                From
              </Typography>
              <DesktopDatePicker
                value={fromDateValue}
                onChange={(value) => {
                  onFromDateChange(dayjs(value).utc().format('YYYY-MM-DD'));
                }}
                slotProps={{
                  textField: {
                    size: 'small',
                    sx: { width: 160, pt: 0.125, pb: 0.125 },
                    name: 'fromDate'
                  }
                }}
              />
              <Typography variant="caption" color="secondary">
                To
              </Typography>
              <DesktopDatePicker
                value={toDateValue}
                onChange={(value) => {
                  onToDateChange(dayjs(value).utc().format('YYYY-MM-DD'));
                }}
                slotProps={{
                  textField: {
                    size: 'small',
                    sx: { width: 160, pt: 0.125, pb: 0.125 },
                    name: 'toDate'
                  }
                }}
              />
              {isFilterApplied && (
                <Tooltip
                  title={<Chip label="Reset filters" variant="combined" color="primary" />}
                  componentsProps={{
                    tooltip: {
                      sx: {
                        backgroundColor: 'transparent',
                        boxShadow: 'none',
                        padding: 0
                      }
                    }
                  }}
                >
                  <IconButton onClick={resetFilters}>
                    <FilterOutlined />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
          </Stack>
        </LocalizationProvider>
        <Box style={{ height: `${gridHeight}px` }} display="flex" flexDirection="column">
          <div className={mode === 'light' ? 'ag-theme-material' : 'ag-theme-material-dark'} style={gridStyle}>
            <AgGridReact<AuditEvent>
              key={refreshKey}
              sideBar={sideBar}
              columnDefs={columnDefs}
              getRowClass={getRowClass}
              defaultColDef={defaultColDef}
              onGridReady={onGridReady}
              onGridPreDestroyed={onGridPreDestroyed}
              pagination={!useScroll}
              rowModelType={'serverSide'}
              cellSelection={false}
              tooltipShowDelay={0}
              tooltipHideDelay={2000}
              paginationPageSize={rowsPerPage}
              suppressColumnVirtualisation={true}
              onPaginationChanged={onPageSizeChanged}
              onColumnResized={saveColumnsStateOnChange}
              onSortChanged={saveColumnsStateOnChange}
              onColumnMoved={saveColumnsStateOnChange}
              onColumnVisible={saveColumnsStateOnChange}
              cacheBlockSize={rowsPerPage}
              paginationPageSizeSelector={[50, 100, 500, 1000]}
              onRowClicked={onRowClicked}
              masterDetail={true}
              detailRowAutoHeight={true}
              detailCellRenderer={detailCellRenderer}
            ></AgGridReact>
          </div>
        </Box>
      </ScrollX>
    </MainCard>
  );
};

export default AuditList;
